import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Search, Save, UserCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, pad } from "@/lib/api";
import { TEMPLATES, tplById, buildBio, BIO_LABEL } from "@/lib/suratTemplates";
import { PageHeader } from "@/components/StatCard";
import { FieldSelect } from "@/components/FieldSelect";
import { SuratDoc } from "@/components/SuratDoc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

import { useAuth } from "@/context/AuthContext";
import { usePejabat } from "@/context/PejabatContext";

export default function BuatSurat() {
  const { isAdmin } = useAuth();
  return isAdmin ? <BuatSuratForm /> : <Navigate to="/surat" replace />;
}

function BuatSuratForm() {
  const { pejabat } = usePejabat();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [jenis, setJenis] = useState(params.get("jenis") || "SKTM");
  const [nik, setNik] = useState("");
  const [warga, setWarga] = useState(null);
  const [bio, setBio] = useState(null);
  const [extra, setExtra] = useState({});
  const [tanggal, setTanggal] = useState(today());
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const tpl = tplById(jenis);

  useEffect(() => {
    if (nik.length !== 16) {
      setWarga(null);
      setBio(null);
      setErr("");
      return;
    }
    api.get(`/warga/nik/${nik}`)
      .then((r) => { setWarga(r.data); setBio(buildBio(r.data)); setErr(""); })
      .catch((e) => { setWarga(null); setBio(null); setErr(errMsg(e)); });
  }, [nik]);

  useEffect(() => {
    setExtra({});
  }, [jenis]);

  const save = async () => {
    if (!warga) return toast.error("Masukkan NIK warga yang terdaftar");
    if (warga.status !== "Aktif" && !["SKK", "SKPG", "SKTB"].includes(jenis)) toast.warning(`Perhatian: warga berstatus ${warga.status}`);
    setSaving(true);
    try {
      const { data } = await api.post("/surat", { jenis, judul: tpl.judul, pola_nomor: tpl.pola, warga_id: warga.id, data: bio, extra, tanggal });
      toast.success(`Surat tersimpan · Nomor ${data.nomor}`);
      nav(`/surat/${data.id}/cetak`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const editKeys = [...tpl.bio, ...(tpl.bioAfter || [])];
  return (
    <div data-testid="buat-surat-page">
      <PageHeader kicker="Layanan Surat Kependudukan" title="Buat Surat">
        <Button onClick={save} disabled={saving || !warga} className="bg-[#0b4f4a] hover:bg-[#072f2c]" data-testid="surat-save-button">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan…" : "Simpan & Cetak"}
        </Button>
      </PageHeader>
      <div className="grid xl:grid-cols-[380px_1fr] gap-6 items-start">
        <div className="bg-white border rounded-xl p-5 space-y-4 xl:sticky xl:top-6">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Jenis Surat</Label>
            <div className="mt-1"><FieldSelect value={jenis} onChange={setJenis} options={TEMPLATES.map((t) => ({ value: t.id, label: t.judul }))} testid="surat-jenis-select" /></div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">NIK Penduduk</Label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={nik} onChange={(e) => setNik(e.target.value.replace(/\D/g, "").slice(0, 16))} placeholder="Ketik 16 digit NIK" className="pl-9 font-mono" inputMode="numeric" data-testid="surat-nik-input" />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">{nik.length}/16 digit — data terisi otomatis</p>
            {warga && (
              <div className="mt-2 flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2" data-testid="surat-warga-found">
                <UserCheck className="h-4 w-4" /> {warga.nama} · RT {pad(warga.rt)}
              </div>
            )}
            {err && <div className="mt-2 flex items-center gap-2 text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2" data-testid="surat-nik-error"><AlertCircle className="h-4 w-4" /> {err}</div>}
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Tanggal Surat</Label>
            <Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="mt-1" data-testid="surat-tanggal-input" />
          </div>
          {tpl.extras.length > 0 && (
            <div className="border-t pt-4 space-y-3">
              <div className="text-xs font-bold tracking-widest uppercase text-amber-700">Isian Tambahan</div>
              {tpl.extras.map((x) => (
                <div key={x.k}>
                  <Label className="text-xs text-slate-600">{x.label}</Label>
                  <Input type={x.type} value={extra[x.k] || ""} placeholder={x.ph} onChange={(e) => setExtra((p) => ({ ...p, [x.k]: e.target.value }))} className="mt-1" data-testid={`surat-extra-${x.k}`} />
                </div>
              ))}
            </div>
          )}
          {bio && editKeys.length > 0 && (
            <details className="border-t pt-4" data-testid="surat-bio-edit">
              <summary className="text-xs font-bold tracking-widest uppercase text-amber-700 cursor-pointer">Koreksi Data (opsional)</summary>
              <div className="space-y-3 mt-3">
                {editKeys.map((k) => (
                  <div key={k}>
                    <Label className="text-xs text-slate-600">{BIO_LABEL[k]}</Label>
                    <Input value={bio[k] || ""} onChange={(e) => setBio((p) => ({ ...p, [k]: e.target.value }))} className="mt-1" data-testid={`surat-bio-${k}`} />
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
        <div className="bg-slate-300/60 rounded-xl p-4 overflow-x-auto" data-testid="surat-preview">
          <div className="mx-auto w-fit shadow-xl origin-top scale-[0.9]">
            <SuratDoc tpl={tpl} bio={bio || { nama: "……………", rt: "……", anggota: [] }} extra={extra} nomor={tpl.pola.replace("{n}", "……").replace("{y}", tanggal.slice(0, 4))} tanggal={tanggal} pejabat={pejabat} />
          </div>
        </div>
      </div>
    </div>
  );
}
